// ============================================================================
// The classification queue — reads and actions. Run B Phase 3, 2026-08-18.
//
// Whatever an AM could not name lands here. The list is a VIEW, not a list this
// file knows: v_classification_queue is every service line recorded as OTHER
// plus every project that arrived without a stated kind. A hardcoded list would
// be stale the first time someone used the escape hatch.
//
// Every action writes a line to the AM. That is not politeness — it is the only
// mechanism by which anyone learns the vocabulary, and Run A's report shows
// OTHER clustering on the same handful of facilities, which is a training
// signal rather than a data problem.
// ============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';
import { addProjectNote, saveProjectServiceLine, sendProjectBackToAm } from '../hooks/use-project-data';

export type QueueKind = 'line' | 'event' | 'service_line';

export interface QueueItem {
  kind: QueueKind;
  item_id: string;
  /** ⛔ NULL on a standing service line — it has no project. */
  project_id: string | null;
  facility_id: string | null;
  facility_name: string | null;
  account_name: string | null;
  words: string | null;
  client_rate: number | null;
  sub_rate: number | null;
  bill_amount: number | null;
  pay_amount: number | null;
  stage: string | null;
  event_class: string | null;
  created_by_eid: string | null;
  when_: string | null;
}

/**
 * A project past Draft has already moved money or is about to. Bouncing it to
 * the AM would hold up billing for a wording problem, and blocking money is
 * exactly what taught everyone to force-fit in the first place. On those, a
 * push-back writes the note and leaves the stage where it is.
 */
export function canBounceToAm(stage: string | null): boolean {
  return stage === 'Draft' || stage === 'Awaiting_AM_Input';
}

export function useClassificationQueue() {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data, error: e } = await supabase
          .from('v_classification_queue')
          .select(
            'kind, item_id, project_id, facility_id, facility_name, account_name, words, client_rate, sub_rate, bill_amount, pay_amount, stage, event_class, created_by_eid, when_',
          );
        if (cancelled) return;
        if (e) throw e;
        const rows = (data ?? []) as unknown as QueueItem[];
        rows.sort((a, b) => (b.when_ ?? '').localeCompare(a.when_ ?? ''));
        setItems(rows);
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, 'Failed to load the classification queue.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return useMemo(() => ({ items, isLoading, error, refetch }), [items, isLoading, error, refetch]);
}

// ---------------------------------------------------------------------------
// Actions. Each one takes the note to the AM and refuses without it.
// ---------------------------------------------------------------------------

interface ExistingLine {
  service_line_id: string;
  scope_text: string | null;
  scope_qty: number | null;
  client_rate: number | null;
  sub_rate: number | null;
  assigned_partner_id: string | null;
  service_date_planned: string | null;
  service_date_actual: string | null;
  notes: string | null;
  line_order: number | null;
}

/**
 * saveProjectServiceLine() writes the WHOLE row — anything not passed becomes
 * NULL. Classifying a line must not silently erase its rates, its sub or its
 * dates, so the current values are read back and passed through. Reading first
 * is deliberate: the alternative is a second write path that knows a different
 * subset of the row, and two write paths disagree eventually.
 */
async function readLine(serviceLineId: string): Promise<ExistingLine> {
  const { data, error } = await supabase
    .from('project_services')
    .select(
      'service_line_id, scope_text, scope_qty, client_rate, sub_rate, assigned_partner_id, service_date_planned, service_date_actual, notes, line_order',
    )
    .eq('service_line_id', serviceLineId)
    .single();
  if (error) throw error;
  return data as unknown as ExistingLine;
}

/**
 * ⛔ EVERY FUNCTION BELOW ACTS ON A PROJECT LINE. Handed a standing service line
 * it would write against a null project id — which is exactly what making the
 * column nullable surfaced: seven call sites, not the one the screen showed.
 * Refuse by name, so the sentence a person sees says which thing went wrong
 * rather than failing somewhere downstream with a null.
 */
function requireProject(item: QueueItem): string {
  if (!item.project_id) {
    throw new Error(
      'This is a standing line on a building, not a project — that action does not apply to it.',
    );
  }
  return item.project_id;
}

function requireNote(note: string): string {
  const trimmed = note.trim();
  if (!trimmed) {
    throw new Error('Write the AM a line — that is how they learn the vocabulary.');
  }
  return trimmed;
}

/**
 * Classify a STANDING service line — a building's own line, not a project's.
 *
 * ⛔ NONE OF THE PROJECT ACTIONS BELOW APPLY TO ONE. A standing line has no
 * project, so there is no billing stage to push it back to, no project note to
 * write and no second line to split into — and every one of those functions
 * would have been handed a NULL project id and written somewhere that does not
 * exist. Adding the third branch to the queue's VIEW without teaching the SCREEN
 * about it was a real break: the row rendered, its reference column read
 * "unknown", and pressing Classify would have gone looking for it among the
 * project lines and not found it.
 *
 * The one thing that can be done is give the line the right answer, through the
 * same door the building's own form uses — which mints nothing, moves no money,
 * and leaves the words the AM wrote on the line underneath the decision.
 */
export async function mapServiceLineToCode(
  item: QueueItem,
  serviceCode: string,
  note: string,
): Promise<void> {
  const trimmed = requireNote(note);
  if (!serviceCode) throw new Error('Pick the kind of work it is.');
  if (!item.facility_id) throw new Error('This line is not on a building, so it cannot be saved.');

  const { data, error } = await supabase.rpc('save_service_line', {
    p_facility_id: item.facility_id,
    p_patch: {
      service_code: serviceCode,
      // The line keeps what the AM wrote AND what was decided about it. The
      // words are the only record of why it went to the queue at all.
      notes: [item.words ?? '', `Classified: ${trimmed}`].filter(Boolean).join('\n\n'),
    },
    p_assignment_id: item.item_id,
    p_sop_id: null,
  });
  if (error) throw error;

  // ⛔ A refusal is not a save. The door answers per field with its own sentence.
  const refused = (data as { refused?: Record<string, string> } | null)?.refused ?? {};
  const sentences = Object.values(refused);
  if (sentences.length > 0) throw new Error(sentences.join(' '));
}

/** Map an unclassified line onto a service we already have. */
export async function mapLineToService(
  item: QueueItem,
  serviceCode: string,
  note: string,
  actorEid: string,
): Promise<void> {
  const projectId = requireProject(item);
  const trimmed = requireNote(note);
  if (!serviceCode) throw new Error('Pick the service this should have been.');
  const existing = await readLine(item.item_id);
  await saveProjectServiceLine(
    projectId,
    {
      service_code: serviceCode,
      scope_text: existing.scope_text,
      scope_qty: existing.scope_qty,
      client_rate: existing.client_rate,
      sub_rate: existing.sub_rate,
      assigned_partner_id: existing.assigned_partner_id,
      service_date_planned: existing.service_date_planned,
      service_date_actual: existing.service_date_actual,
      notes: existing.notes,
      line_order: existing.line_order,
    },
    item.item_id,
  );
  await addProjectNote(projectId, `Classified: ${trimmed}`, actorEid);
}

/**
 * Two things happened — record them separately.
 *
 * The second line deliberately carries no rates. Money was recorded once, as one
 * amount, and splitting it would be inventing a division nobody agreed. With one
 * line unrated the project can no longer be itemised, so the client sees the
 * project total on a single line naming both services — which is exactly what
 * was actually agreed.
 */
export async function splitLine(
  item: QueueItem,
  firstServiceCode: string,
  secondServiceCode: string,
  secondScopeText: string,
  note: string,
  actorEid: string,
): Promise<void> {
  const projectId = requireProject(item);
  const trimmed = requireNote(note);
  if (!firstServiceCode || !secondServiceCode) throw new Error('Pick both services.');
  const words = secondScopeText.trim();
  if (!words) throw new Error('Say what the second line was, in words.');
  const existing = await readLine(item.item_id);
  await saveProjectServiceLine(
    projectId,
    {
      service_code: firstServiceCode,
      scope_text: existing.scope_text,
      scope_qty: existing.scope_qty,
      client_rate: existing.client_rate,
      sub_rate: existing.sub_rate,
      assigned_partner_id: existing.assigned_partner_id,
      service_date_planned: existing.service_date_planned,
      service_date_actual: existing.service_date_actual,
      notes: existing.notes,
      line_order: existing.line_order,
    },
    item.item_id,
  );
  await saveProjectServiceLine(projectId, {
    service_code: secondServiceCode,
    scope_text: words,
    assigned_partner_id: existing.assigned_partner_id,
    service_date_planned: existing.service_date_planned,
    service_date_actual: existing.service_date_actual,
    line_order: (existing.line_order ?? 1) + 1,
  });
  await addProjectNote(projectId, `Split into two service lines: ${trimmed}`, actorEid);
}

/**
 * This needs a service code we do not have. Goes to Ben through the app's own
 * feedback channel — the one he already triages — rather than a new table
 * nobody would remember to look at.
 */
export async function escalateForNewCode(
  item: QueueItem,
  note: string,
  actorEid: string,
): Promise<void> {
  const trimmed = requireNote(note);
  // ⛔ A standing line has no project. Before this, the id and the link were built
  // from item.project_id regardless, so an escalation raised from one would have
  // read "null (ASN-00275)" and pointed at /project-work/null.
  const reference = item.project_id ?? item.item_id;
  const where = item.project_id
    ? `/project-work/${item.project_id}`
    : `/account-management/facilities/${item.facility_id}`;
  const { error } = await supabase.rpc('submit_feedback', {
    p_type: 'request',
    p_source: 'classification_queue',
    p_title: `New service code needed — ${item.facility_name ?? reference}`,
    p_description:
      `${reference} (${item.item_id})\n\nWhat the AM wrote: ${item.words ?? '(nothing)'}\n\n` +
      `Classifier's view: ${trimmed}\n\nCreate a service_catalog code, or say which existing code this maps to.`,
    p_module: 'project-work',
    p_url_path: where,
    p_chat_excerpt: null,
    p_user_agent: null,
  });
  if (error) throw error;
  // Only a project has a note trail to write to.
  if (item.project_id) {
    await addProjectNote(
      item.project_id,
      `Escalated for a new service code: ${trimmed}`,
      actorEid,
    );
  }
}

/**
 * Not enough detail to call it. Always writes the note; only moves the billing
 * stage when the project has not already gone to finance. See canBounceToAm().
 */
export async function pushBackToAm(
  item: QueueItem,
  note: string,
  actorEid: string,
): Promise<{ stageMoved: boolean }> {
  const projectId = requireProject(item);
  const trimmed = requireNote(note);
  await addProjectNote(projectId, `Needs more detail to classify: ${trimmed}`, actorEid);
  if (!canBounceToAm(item.stage)) return { stageMoved: false };
  await sendProjectBackToAm(projectId, trimmed, actorEid);
  return { stageMoved: true };
}

/** Classify a whole event that arrived without a stated kind. */
export async function classifyEvent(
  item: QueueItem,
  eventClass: string,
  reasonCode: string | null,
  note: string,
  actorEid: string,
): Promise<void> {
  const projectId = requireProject(item);
  const trimmed = requireNote(note);
  if (!eventClass) throw new Error('Pick what kind of event this was.');
  const { data, error } = await supabase
    .from('projects')
    .update({
      event_class: eventClass,
      reason_code: reasonCode,
      reason_note: trimmed,
      updated_by_eid: actorEid,
    })
    .eq('project_id', projectId)
    .select('project_id');
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("That classification didn't save — you may not have permission on this project.");
  }
  await addProjectNote(projectId, `Classified: ${trimmed}`, actorEid);
}
